import React from "react";

export const turboSculpture = {
  header: {
    top: ["Turbo-sculpture ", "&"],
    mid: "anti-monumentalism",
    bottom: <>The case of Banatski Sokolac's Bob Marley monument</>,
  },
  nav: [
    { variant: "header", copy: "contents" },
    { variant: "list", copy: "Introduction", linkTo: "intro" },
    {
      variant: "list",
      copy: (
        <>
          A fighter for freedom
          <br /> from Banatski Sokolac
        </>
      ),
      linkTo: "main",
    },
    { variant: "list", copy: "Conclusion", linkTo: "conclusion" },
    { variant: "subheader", copy: "Addenda:" },
    { variant: "list", copy: "What is turbo-folk?", linkTo: "turbofolk" },
    {
      variant: "list",
      copy: "What is turbo-sculpture",
      linkTo: "turbosculpture",
    },
    { variant: "reference", copy: "Endnotes", linkTo: "endnotes" },
    { variant: "reference", copy: "Bibliography", linkTo: "bibliography" },
    { variant: "reference", copy: "Colophon", linkTo: "colophon" },
  ],
  sections: [
    {
      header: { section: "intro", title: "Introduction" },
      body: {
        variant: "paragraphWithEndnotes",
        copy: [
          <>
            In the small village community of Banatski Sokolac, Serbia, an
            unusual monument stands. A life-size sculpture of reggae legend Bob
            Marley, cast in painted polyester, raises its right fist in a
            gesture of unity and comradeship. At its base, an inscription reads
            “Bob Marley – fighter for freedom armed with a guitar.”[1]
          </>,
          <>
            What is curious, perhaps, is that this is not an isolated
            phenomenon. Prior to Bob Marley’s unveiling, a Rocky Balboa monument
            was erected in the town of Žitište, Serbia, as well as a Bruce Lee
            monument in Mostar, Bosnia.[2] If we dig a little, we will discover
            that these monuments are often described with the term{" "}
            <i>turbo-sculpture</i>, thus positioning them in relation to{" "}
            <i>turbo-folk</i>—and furthermore, one can discover many similar
            proposed projects that never came to fruition: a Tarzan (as
            portrayed by Weissmuller) in the village of Međe, a Samantha Fox in
            Čačak, and a Tupac Shakur in Belgrade, to name a few.[3] One might
            wonder, where did this trend of monumentalising figures from pop
            culture come from? Is it simply a desperate cash grab, an attempt to
            lure tourists in? A short-term project to create jobs, move money
            around? Is it indicative, as some might claim, of a moral and
            aesthetic decline?
          </>,
          <>
            On the contrary: in this text, I will argue that monuments such as
            these are more than kitsch curiosities. Instead, they are emblematic
            of a struggle with traumatic history and against divisive constructs
            of heritage and national identity. Furthermore, I suggest that the
            visual language employed is not entirely accidental, but rather
            indicative of a critical position towards state-sanctioned culture
            and cultural production—of the intelligentsia and the fabricators of
            the aforementioned divisive national heritage.
          </>,
          <>
            Using Banatski Sokolac’s Marley as the primary example, I will
            examine the category of <i>turbo-sculpture</i> in relation to the{" "}
            <i>monument</i> and <i>monumentality</i>, in order to investigate if
            and how turbo monuments disrupt canonical narratives of national
            heritage. This essay will move from a brief overview of turbo-folk
            to turbo-sculpture in particular, followed by a brief examination of
            the Marley monument and the context it emerged from, in order to
            come to the potential subversive power of turbo-sculpture against
            dominant understandings of national heritage.
          </>,
          <>
            This essay was originally written in 2019 within the MFA in Fine
            Arts at HDK-Valand, University of Gothenburg, with the title{" "}
            <i>
              Turbo-sculpture: The case of Banatski Sokolac’s Bob Marley
              monument
            </i>
            . It is presented here, revised and edited, for the occasion of the
            exhibition <i>Monument to Balkan Brotherhood</i> in DC Krov,
            Belgrade, 2021.
          </>,
        ],
      },
    },
    {
      header: {
        section: "main",
        title: (
          <>
            A fighter for freedom
            <br /> from Banatski Sokolac
          </>
        ),
      },
      body: {
        variant: "paragraphWithEndnotes",
        copy: [
          <>
            As mentioned in the introduction, the monument in Banatski Sokolac
            is a life-sized full-body depiction of reggae legend Bob Marley,
            cast in polyester and subsequently painted. He raises his left fist
            in a militant salute, presumably rallying those around him to join
            in the fight “for freedom,'' as the inscription on his pedestal
            suggests. This piece was commissioned for the 2008 instalment of{" "}
            <i>Rock Village</i>, a music festival native to Banatski Sokolac,
            and created by Croatian artist Davor Dukić.
          </>,
          <>
            Banatski Sokolac itself is a rather small settlement, housing only
            272 inhabitants, located in the semi-autonomous province of
            Vojvodina in northern Serbia. Over the past century it has suffered
            majorly from depopulation, as the poor state of the local economy
            and the greater trend of urbanisation pushed people out of the
            countryside and into the major cities.[4] Flooding in 2005 caused
            extensive infrastructural damage and destroyed many of the crops the
            locals relied on; to raise money for repairs, some of these locals
            decided to organise a cultural event with the help of friends of the
            community and musicians. Thus <i>Rock Village</i> was created and
            held for the first time the same year.
          </>,
          <>
            The unveiling ceremony for the Bob Marley monument took place at
            18:00 on August 23rd, the second day of the festival,[5] in a
            hyped-up event in which both Croatian and Serbian musicians were
            present, many of them well-known fixtures in their scenes.[6] Over
            ten years later, the festival is still strongly associated with the
            Marley statue, and a photograph of it is prominently featured on the
            festival’s “about” page.[7] News of the peculiar monument made
            international headlines, with a <i>Guardian</i> article loudly
            proclaiming “Unveiling of Bob Marley statue unites Serbs and
            Croats”.[8] While this particular article might be somewhat
            tongue-in-cheek, it is obvious that this was the intention of this
            endeavour—at least to an extent. In addition to employing the image
            of a musician famous for speaking out in favour of peace, love, and
            unity, simply having both Serbian and Croatian public figures
            joining forces, barely a decade after the conclusion of a war that
            tore their countries apart, is clearly meant to convey a message of
            unity and reconciliation.
          </>,
          "IMAGES",
          <>
            This in and of itself is not particularly ground-breaking,
            admittedly. One may be tempted to read it as a platitude very
            typical of public art commissioning: what else could a public
            monument promote anyway, other than some vague notion of
            “togetherness”? And what could an unveiling ceremony be, other than
            an opportunity for various minor celebs to shake hands in front of a
            camera? These judgements are not unfounded—however, I would argue
            that there is most definitely <em>more</em> going here, as evidenced
            by the undoubtable trend of turbo-sculptures I mentioned previously.
            There is, I believe, a genuine search for new, alternative ways to
            memorialise and to commission public art—a search for aesthetics
            capable of representing a vastly heterogeneous society which is
            still recovering from a decade-long war.
          </>,
          <>
            In fact, this is what Sokolac’s Marley has in common with Žitište’s
            Balboa and Mostar’s Bruce Lee: as unlikely as it might sound, all
            three of these projects were framed as attempts to promote unity
            against ethnic divisions. To quote Veselin Gatalo of Urban Movement
            Mostar: "We will always be Muslims, Serbs or Croats. But one thing
            we all have in common is Bruce Lee."[9] The irony of this statement
            is that within merely a few days, the Bruce Lee monument was
            vandalised and subsequently had to be removed from public view until
            2013.[10]
          </>,
          <>
            The idea (or perhaps more appropriately, <em>hope</em>) expressed
            here is that, as pre-existing native cultural symbols and historical
            figures are so <em>intensely</em> charged with meaning, are so bound
            to the history of strife in the region, are so often contested,
            external figures and symbols must be appropriated to serve as common
            points of reference. It is no coincidence that all three of these
            figures emerged from the American pop-culture of the last fifty
            years: they are often perceived as international, a-historical,
            “pure”. To further illustrate this, another citizen of Mostar had
            this to say when asked about the Bruce Lee monument:
            <blockquote style={{ marginTop: "2em", marginBottom: "2em" }}>
              <i>
                the purpose here is to understand Lee as a clean human! I mean …
                he’s … not Croatian, nor a Serb … he is not a Catholic, not
                Orthodox, not Muslim … what a relief!!! A
              </i>{" "}
              <span>clean</span>{" "}
              <i>
                human, finally! I am sick and tired of all these political
                discussions … how are we going to name our streets … and
                airports … well, it is a problem that they wanted to name
                Sarajevo airport after Alija Izetbegovic, a war-criminal! I say:
                let’s pick some more global popular icons that stand for peace
                and bring them here — like, we could name our airport after John
                Lennon … why not?
              </i>{" "}
              <span
                className="endNote"
                title="11.  Z. Volcic, ‘The Struggle to Express, Create and Represent in the Balkans’, in H. K. Anheier, Y. R. Isar (eds.), Cultural Expression, Creativity and Innovation, 2010, SAGE Publications, vol. 3, p 163."
              >
                [11]
              </span>
            </blockquote>
            Some perceive this a short-sighted, potentially dangerous move;
            Milica Tomić, a Serbian artist and vocal opponent of
            turbo-sculpture, called the monuments “a dangerous joke” and warned
            that “history is being erased and replaced by Mickey Mouse,”[12]
            something that Čvoro also describes as “the emptying-out of
            history.”[13] In defanging the traumatic past, in turning it into
            something abstract, it is also turned into something much more
            palatable and something much easier to ignore.
          </>,
          <>
            However—is that not true for <em>all</em> monuments relating to war?
            Aren’t all of them (or at least the overwhelming majority) a)
            abstract, or b) depict the personification of some vague ideal (such
            as <em>victory</em> etc.), or are c) text-based, declaring the site
            in question as the ground of this or that battle? And do not these
            monuments eventually fade into further abstraction, as they
            gradually lose impact? Does the act of monumentalising (particularly
            through traditional means and aesthetics) a traumatic event{" "}
            <em>actually</em> facilitate remembrance, awareness and discussion
            of the traumatic event in question? Isn’t it more accurate to
            acknowledge that genuine discourse over these types of events is
            conducted only whenever some “problematic” proposal is made
            public—such as a Bob Marley statue? I would suggest the honest
            answer is “yes” to all these questions.
          </>,
          <>
            Is it also not the right of the members of a community to judge for
            themselves what their own needs are in the reconciliatory effort?
            The quote I presented above demonstrates a clear exasperation with
            the oversaturation of public life with the legacy of war and the
            seemingly impassable deadlock. “A <em>clean</em> human, finally!”
            While it would be wrong to assume that there are no critical voices
            within the community, this and other recorded statements from Mostar
            point to an overwhelming need for something, <em>anything</em>, that
            can serve as the signifier of a “clean” beginning.[14]
          </>,
          <>
            The desire for a “clean” symbol and point of reference becomes even
            more understandable when we consider the ever-present feeling of{" "}
            <em>entrapment in history</em> which permeates Balkan literature and
            art. Notable examples of this can be found in film, such as Theo
            Angelopoulos <i>Ulysses’ Gaze</i> (1995), in which the protagonist,
            in his search for three reels of film from the beginning of the
            century containing the “first innocent gaze of the Balkans”, embarks
            on a Homeric journey through the past ninety years of war in which
            past and present are hopelessly entangled. The mythistorical
            narrative Angelopoulos wove together is entirely cyclical, and
            almost claustrophobic in its apparent lack of any exit point.
            Similar storytelling can also be seen in the seminal{" "}
            <i>Lepa Sela Lepo Gore</i> (Srđan Dragojević, 1996) and (the highly
            criticized) <i>Underground</i> (1995) by Emir Kusturica.[15]
          </>,
          <>
            In addition to the above, and to move from mythistory back into
            “plain” history, it is also true that all Balkan nation-states
            emerged from war; and that the constructs of nation and national
            identity in this region are intrinsically tied to narratives of war
            and/or violent uprising. From early school education onwards,
            carefully curated histories of territorial conflict and the
            glorification of military violence form the bedrock of all efforts
            to instill a “national consciousness” in the young.[16] Furthermore,
            military and paramilitary leaders are heroised and ubiquitously
            venerated as forefathers of the Nation; monumentalised and presented
            as examples to follow for their sacrifices to the motherland,
            despite their often sordid past.[17]
          </>,
          <>
            This was the case even for the commanders and war criminals of the
            last Yugoslav wars, which were more often than not criminals in
            their previous lives as well.[18] Through this lens, the choice to
            “import” Bob Marley or any of the other “pure” figures memorialised
            in turbo-sculpture is better understood as an attempt to break out
            of the vicious circle of war, trauma and violence.
          </>,
          <>
            Additionally, it can be argued that artworks depicting figures from
            pop culture are in fact more successful in representing working
            class memory. Although conceptually incongruent, Western pop culture
            and Yugoslav socialism are strongly linked: the prosperity ushered
            by socialist modernisation enabled the development of popular
            culture and the import of Western cultural production. For this
            reason Čvoro argues that 1970s Western pop culture can serve as a
            mediator of memories of socialism. He describes the peculiarity of
            Yugoslav culture with: “Politically, it was socialist yet
            consumerist […] and aesthetically, it was caught between socialist
            realism and Western postmodernism”.[19] The aesthetics of western
            consumerist culture and globalisation are thus transformed into the
            aesthetics of prosperity and unity—a unity which acknowledges and
            incorporates multiplicities and hybrid subjectivities. The success
            of turbo-folk as a genre and aesthetic in the Balkans lies greatly
            in this fact: that it unabashedly appropriates, incorporates and
            synthesizes multiple disparate elements to create something hybrid.
          </>,

          <>
            Furthermore, with this act the village community of Banatski Sokolac
            has claimed not only the right to their own symbolic language, but
            also to the use of their own public space. Although this echoes
            Yugoslav ‘self-management socialism’, during which communities were
            encouraged to take ownership of social property and would often
            self-organize to commission monuments,[20] there is still an
            undeniable contrast here with another Yugoslav legacy, namely that
            of the numerous <em>spomenici</em> strewn across ex-Yugoslavia. For
            the purposes of comparison, I will briefly outline their
            characteristics here.
          </>,
          <>
            Better known to the Anglophone public as <em>spomenik</em> or
            sometimes <em>spomeniks</em>, they are monuments erected during
            Tito’s regime, mostly to commemorate the achievements of his
            partisan army during World War II. Their overarching purpose was to
            foster and promote the most important of Yugoslav ideals,
            ‘Brotherhood and Unity’, representing a constructed history of
            multi-ethnic harmony and a fictive <em>shared</em> struggle against
            fascism.[21] [22] Spomenici are invariably imposing in their massive
            scale and austere in their materials, usually concrete and more
            rarely stone or steel. They are all also very noticeably abstract—
            <em>socialist modernist</em>—in their designs, in clear and
            intentional opposition to Soviet socialist realism. The numerous
            (several thousands; the precise number is unknown) monuments were
            constructed in the Yugoslav countryside, often on the sites of
            battles, and tours of these were a core element of patriotic
            education for the young.[23] [24] Their remote locations, surrounded
            by nature, combined with their imposing dimensions, were meant to
            present an image of the Yugoslav project as larger-than-life,
            immutable and eternal. As James E. Young so aptly writes: “By
            suggesting themselves as kin to the landscape’s geological
            outcroppings, monuments would cast a nation’s ideals and founding
            myths as naturally true as the landscape in which they stand”.[25]
            Now however, many lie disused and unkempt in their sites; some
            vandalised or even completely destroyed. Rather than symbolising the
            ideals of a multicultural utopia, they are relegated to stand as sad
            evidence of Yugoslavia’s failure.
          </>,
          <>
            This is important to keep in mind, not only because spomenici
            saturate the cultural landscape our Bob Marley exists in (having
            featured even on banknotes), but because the Marley monument is
            itself described with the word <em>spomenik</em> in Serbian
            references.[26] It is not presented as a prop or set-dressing for
            Banatski Sokolac’s festival, even if it might have started its life
            that way—it is put forth as a monument. Now, it is not unreasonable
            to suspect that this descriptor is somewhat tongue-in-cheek. But
            even so, merely suggesting that two meters of sloppily painted
            polyester are comparable to any of the concrete goliaths of Tito’s
            Yugoslavia is a subversive move.
          </>,
          <>
            If our Marley is presented as a spomenik in earnest, then we have a
            radical affirmation of the right of a community to make decisions
            regarding the management of their heritage and history, as well as
            on the aesthetic language through which this heritage shall be
            communicated. In this case, the Marley monument simply with its
            difference constitutes an emphatic rejection of state-sanctioned
            culture, heritage and understandings of national identity. At the
            same time, it would be amiss to not point out that both Tito’s
            spomenici and Sokolac’s Bob Marley are monuments to the ideals of
            unity and peace. Is our Bob destined to have the same fate, to
            become a monument to a failed idea? And, to return for a moment to
            the inscription at his base: “Fighter for freedom armed with a
            guitar”. Even while employing the image of a <em>pacifist</em> for a{" "}
            <em>pacifist</em> narrative, somehow references to <em>war</em>{" "}
            still creep in. Is a break from the cycle of violence even possible?
          </>,
          <>
            On the other hand, if the Marley is proposed as a monument “only” as
            a joke, then it has still succeeded in poking fun at those in charge
            of cultural policy in this region, and those interested in
            maintaining the status quo. It is <em>still</em> critical of
            state-sanctioned culture, perhaps even more so. Such critical
            strategies are usually limited to the domain of art; in fact, this
            makes it directly comparable to Komar and Melamid’s{" "}
            <i>Monumental Propaganda</i> project from 1992. In{" "}
            <i>Monumental Propaganda</i>, the artist duo proposed to repurpose
            as many Soviet monuments as possible to save them from impending
            destruction: “to make a kind of ‘Mardi Gras Carnival of Monuments’”
            as they wrote in their letter to Boris Yeltsin.[27] The
            modifications to be performed on these Soviet relics were often
            impossible and always humourous, such as moving a monument (
            <i>Worker and Kolkhoz Woman</i>) a few meters forward on its base,
            so that the depicted heroic couple appear to be blindly stepping
            into the void. To quote Young again: “they have resorted to the most
            lethal of critical methods: play and whimsy. For as it turns out,
            the surest antidote to the traditional high-seriousness of monuments
            is below-the-belt parody. […] Nothing looks so absurd and foolish as
            a monument out of its element, its time and place”.[28] The Marley
            achieves this not by directly modifying any existing monuments, but
            by positioning itself in direct comparison to them, thereby lowering
            their status—as, by virtue of employing the visual language of{" "}
            <em>turbo-sculpture</em>, it embodies low-brow popular culture.
          </>,
          <>
            Additionally, simply by existing in such contrast to our
            understanding of what a monument is, it inadvertently manages to
            raise myriads of questions. <em>Why</em> are monuments the way they
            are? Who decides? Why are they so similar aesthetically? Why is it
            so irreverent to put a plastic Bob Marley on a pedestal and declare
            it a monument? Here we might begin to consider Banatski Sokolac’s
            Marley as a <em>counter-</em> or, rather, <em>anti-monument</em>.
          </>,
          <>
            The concept of the <em>counter-monument</em> was introduced to the
            anglophone context through J. E. Young’s essays on Holocaust
            memorialisation, and notably his 1992 piece “The counter-monument:
            Memory against itself in Germany today” published in{" "}
            <i>Critical Inquiry</i>.[29] The term counter-monument is his
            translation of <em>gegendenkmal</em>, a concept developed in 1980s
            West Germany to describe a monument or artwork in direct dialogical
            relationship to a pre-existing nearby monument; in other words, a
            monument that constitutes a critical reply to another one, with the
            juxtaposition of the two bringing forth a new meaning. However,
            Young’s use of the term included monuments which more broadly
            demonstrated a lack of traditional monumental characteristics,
            either in their form or in the subject they depicted. He was
            subsequently heavily criticised for his lack of nuance by his
            German-speaking peers, and the term <em>anti-monument</em> was
            eventually introduced to describe the works he was referring to. If
            an anti-monument can be claimed to exist in a dialogical
            relationship with something, it is with the broader ideas of{" "}
            <em>monument</em> and <em>monumentality</em>. This is precisely what
            I would suggest our Marley is doing, and the crux of this essay.
          </>,
          <>
            Most anti-monuments oppose conventional monumental form: they are
            impermanent instead of permanent, fragile instead of solid,
            horizontal rather than vertical, fragmented rather than singular and
            orderly. However, it is also possible for an anti-monument to simply
            represent (and thus, honour) non-conventional subjects or
            marginalised identities, such as Marc Quinn’s{" "}
            <i>Alison Lapper Pregnant</i> (2005). Additionally, an anti-monument
            might incorporate conventional monumental forms “for their ironic or
            jarring effects, when applied to unfamiliar themes or unconventional
            locations”.[30]
          </>,
          <>
            The Bob Marley sculpture manages to very carefully ride the line
            between conventional monumentality—being figurative, representing an
            ideal almost trite in its overuse—and <em>anti</em>
            -monumentality—the unconventional subject, the garish colours, the
            general camp-ness. This is, I would argue, why it’s so fascinating.
            Its original intention is hard to decipher, but it nonetheless
            manages to position itself critically towards so many traditions not
            often examined in broader society; history, heritage, the Nation,
            the monument, public art. And while the first reaction of many might
            be to ridicule its apparent naiveté, at the end of the day it
            remains the charming and cherished centrepiece of its community.{" "}
          </>,
        ],
      },
    },
    {
      header: {
        section: "conclusion",
        title: "Conclusion",
      },
      body: {
        variant: "paragraph",
        copy: [
          <>
            What I have done in this essay is to bring together various concepts
            in relation to which we can consider turbo-sculpture, a form which
            is often examined only in relation to turbo-folk, if examined at
            all. Turbo-sculpture is, unfortunately, still severely
            under-theorised; relegated to the rare online article, as an
            entertaining curiosity to be soon forgotten again. Here, I have
            attempted to enrich the discourse around it, and to bring forth
            considerations regarding its function; which will be of particular
            interest and use to the reader interested in artistic strategies in
            this field.
          </>,
          <>
            Through this monument to Bob Marley and the discourse around it, we
            explored the search for a new symbolic language in the region, for a
            new constellation of heroes; this search being a direct consequence
            of the trauma left by the last Yugoslav wars and the collapse of
            Yugoslavia. We also examined the Marley monument in relation to the
            history of monumental works in ex-Yugoslavia, especially the mammoth
            spomenici commissioned by Tito’s government which played a prominent
            role in the construction and propagation of a national narrative. In
            its (relatively) diminutive polyester form, it lies in stark
            contrast with the established notions of what a monument should
            be—and especially in relation to the legacy of the spomenici, it
            appears as a complete negation of that heritage: thus rendering it
            an anti-monument. We finally proceeded to further look at Banatski
            Sokolac’s Marley in relation to the concept of the anti-monument, a
            monument critical towards monumentality itself and toward the
            utilisation of artistic production to serve the interests of the
            state.
          </>,
          <>
            When I started this research, I thought the Marley monument was just
            funny. And it’s not just me—I often get a chuckle or two whenever I
            bring it up in discussions or presentations. Maybe it <i>is</i>{" "}
            ridiculous; or, perhaps, <i>uncomfortable</i>? Its playfulness most
            definitely seems at odds with its context, and by being so, it
            inevitably brings to light all the reasons why it seems out of
            place: it stands in stark contrast with the continuing legacies of
            nationalism and war. Whether this was intentional on behalf of the
            artist, I am not able to say; and after twenty pages of analysis, I
            am inclined to believe that it doesn’t matter. What matters is that
            these legacies are brought to the forefront, and that discussion is
            had around them. And maybe a polyester Bob Marley is the best lure
            into this discussion.
          </>,
        ],
      },
    },
    {
      header: {
        section: "turbofolk",
        title: "What is turbo-folk?",
        preHeader: "Addendum 1:",
      },
      body: {
        variant: "paragraphWithEndnotes",
        copy: [
          <>
            In the following paragraphs, I will attempt to provide a very brief
            overview of turbo-folk and its surrounding context. It should be
            noted however that turbo-folk is a vastly complicated conceptual
            category with a complicated history, and that a truly detailed
            analysis of any one facet of it is well beyond the scope of this
            short essay. Therefore, the information presented here on turbo-folk
            is merely meant to familiarise the Western* reader with its core
            elements and the discourses around it.
          </>,
          <>
            Turbo-folk is originally and primarily a type of music that brings
            together high octane electronic pop and traditional folk music,
            incorporating Slavic, Mediterranean and Oriental elements.[31]
            Emerging in the nineties, it is considered to be a development of{" "}
            <i>novokomponovana narodna muzika</i> (i.e. newly composed folk
            music; often shortened to NCFM), which in turn is a product of the
            ‘70s and the global rise of world music.[32] As such, the “typical”
            NCFM sound is the combination of “acoustic instruments
            characteristic of regional traditions (e.g., tapan and darabuka
            drums)”, with “new instruments”, namely “electric guitar and bass,
            keyboards and electronic drums.”[33] Turbo-folk inherits these
            features, while “tapping into dance music which was sweeping Western
            Europe at the time. <i>Turbo</i> implied speed, power and modernity,
            while folk represented the remaining semblance of traditional{" "}
            <i>melos</i> (most commonly expressed through the oriental trilling
            of the female voice).”[34]
          </>,
          <>
            There is an evolution regarding the lyrical content as well. NCFM
            songs were overwhelmingly about “love, regional belonging, family
            and everyday life”, framing this genre within the <em>rural</em>
            .[35] In its combination of techno-pop with these themes, it is
            emblematic of the urbanisation process which was taking place in
            Yugoslavia at the time. Turbo-folk incorporates these tropes as well
            (and in fact makes frequent references to rural life), but gradually
            shifted further into the “urban”, depicting a lifestyle of fast
            cars, clubs, sexual liberation, and in general “the good life”.[36]{" "}
          </>,
          <>
            These thematics are reflected in the aesthetics of the genre. While
            typical ‘70s-‘80s glam is prominent in NCFM, in turbo-folk it gives
            way to a more urban aesthetic; depicting an urban working class
            indulging in materialism and excess, with cars, money, glamour,
            hyper-sexualised women, clubs and parties taking centre-stage. This
            was combined with the low-quality naïve music videos which were
            rapidly churned out by <i>TV Palma</i> (known also as the “Serbian
            MTV”), a populist TV station which was the first in the region to
            specialise as music television.[37]
          </>,
          <>
            It is perhaps not surprising then that turbo-folk was most popular
            among the working classes, which in addition to the{" "}
            <em>nouveaux riches</em>, constituted its audience. It provided an
            escape into glamour and kitsch, while essentially supporting
            traditional values. Consequently, it quickly became an instrument of
            nationalist propaganda.
          </>,
          <>
            It is very important to note that turbo-folk is largely associated
            with Serbian nationalism and Milošević’s regime in particular. This
            is in great part due to the personal affiliations of some major
            turbo-folk stars, such as singer Ceca’s marriage to paramilitary
            leader Arkan, a warlord “responsible for some of the worst killings
            of non-Serbs” in the ‘90s.[38] In fact, turbo-folk is often
            presented in opposition to rock music, a genre associated with
            anti-war discourse—and the belief that the Milošević regime actively
            promoted turbo-folk in order to suppress rock culture and activism
            is not uncommon, although not quite true.[39] As is true for most
            aspects of turbo-folk, its relationship with the state is also
            rather complicated: although it was appropriated early on and used
            to serve a Serbian nationalist agenda, eventually Milošević began to
            actively distance himself from it in 1994, when appealing to the
            international community as a “champion of peace” became more
            desirable than winning over the population.[40]{" "}
          </>,
          <>
            In addition, turbo-folk was being seen as an erosive influence on
            local culture with its bricolage of folk, oriental and techno
            elements, and was further accused of contributing to the decay of
            “moral and ethical values”.[41] To this day, typical
            characterisations of this genre are:{" "}
            <em>
              “trash,” “banal,” “pornographic,” “(semi-)rural,” “oriental” and
              “Balkan”
            </em>
            .[42]{" "}
          </>,
          <>
            However, these descriptors aren’t off-putting for many. By
            intentionally adopting these labels, many artists play with
            intentional self-exoticisation, simultaneously critiquing and
            indulging in depictions of the so-called Balkan temperament—lively,
            spontaneous, passionate, emotive, cheeky, hedonistic.[43] This is
            particularly noticeable in Slovenia, where local turbo-folk
            production is especially playful and self-parodying.[44] The kitsch
            aesthetics of turbo-folk can very easily turn to <em>camp</em>,
            consciously adopting a certain aesthetic in order to produce a
            passive critique of norms. Additionally, when turbo-folk operates in
            this mode of self-parody, it is simultaneously parodying everything
            associated with it: nationalisms, heteropatriarchal traditions,
            materialism. It is therefore not surprising that, currently,
            turbo-folk has a strong presence in the queer scenes of the region,
            frequently featuring in drag shows.[45]
          </>,
          <>
            In exactly its self-awareness and self-parody, turbo-folk
            incorporates anti-nationalist expressions. An example of this can be
            seen in the film <i>Parada</i> (The Parade, 2011), directed by Srđan
            Dragojević.[46] A Serb, a Croat, a Bosnian (all old enemies from the
            war) and a homosexual man (his identity as a gay man overriding any
            other he might have) are all united in exalted song when{" "}
            <i>Ne Može Nam Niko Ništa</i> (tr.: <i>No One Can Touch Us</i>, a
            turbo-folk classic from 1989) comes on the radio. Here, turbo-folk
            is portrayed as a powerful unifying agent; its mixing of elements
            from various cultures renders it a common point of reference.
          </>,
          <>
            The current resurfacing interest in turbo-folk within the academic
            community comes largely from this understanding of turbo-folk as a
            unifying agent. While I suspect that for many this position is most
            likely rooted in Yugo-nostalgia, I <em>do</em> believe that within
            artistic explorations of turbo-folk there is ground for fruitful
            discourse on the subject of Yugoslav (and perhaps more broadly,
            Balkan) nationalism. Having been the subject of both academic
            analysis and popular debate for nearly three decades, there is now a
            greatly increased awareness of the multi-layered complexity of this
            genre, and contemporary performers and artists actively delight in
            “creating these impossible semiotic mixtures” within this context
            where “everything turns out to be something else”.[47] I would
            suggest that this ever-present instability provides, ironically, a
            stable point of reference, acting as an <em>equaliser</em> of sorts;
            which partially explains turbo-folk’s transnational appeal.
          </>,
          <>
            In relation to the above I would argue that the immense complexity
            of turbo-folk as a cultural signifier renders it a very useful tool
            for artists who are working with the similarly complicated subjects
            of tradition, heritage, memory and national identity within the
            region of ex-Yugoslavia. Simultaneously, its camp and kitsch
            aesthetic leaves space for humour and playfulness, elements which
            help facilitate discussion by neutralising anything potentially
            volatile. I would point to the current appropriation and use of
            turbo-folk by queer artists and performers in the Balkans as an
            example of this already happening. As Catherine Baker so aptly puts
            it, turbo-folk acts as “a conceptual category which aggregates
            connotations of banality, foreignness, violence and kitsch in order
            to provide critical apparatus with a ready-made strategy of
            distancing.”[48]
          </>,
          <>
            To end this segment, and in relation to the above, I would propose
            that turbo-folk has the unique potential to act as a cultural
            translator. Having emerged as a major (in its longevity and
            transnational recognisability) cultural product of a multicultural
            and multilingual region, it also operates in an “in-between” space,
            being neither here nor there, yet simultaneously mediating multiple
            cultural contexts. I am relating it to Tomislav Longinović’s idea of
            a cultural translation which is hybrid, multicultural and capable of
            producing new meanings—mobile and aware of the impossibility of an
            absolute identity.[49] It bears notice that turbo-folk has outlived
            the context that gave birth to it; and it is telling that many are
            looking at it today with renewed interest. While we’re looking for
            an antidote to the constant rise of nationalism across the world,
            and while the concept of the nation-state seems less and less
            reasonable, the ability of turbo-folk to communicate across
            languages and cultures while maintaining its aforementioned
            hybridity seems an example to follow.
          </>,
        ],
      },
    },
    {
      header: {
        section: "turbosculpture",
        title: "What is turbo-sculpture?",
        preHeader: "Addendum 2:",
      },
      body: {
        variant: "paragraphWithEndnotes",
        copy: [
          <>
            The definition of turbo-folk given above is focused on the music
            genre and its contextualisation. It is indeed true that the prefix{" "}
            <em>turbo</em>- is overwhelmingly used to describe the genre and its
            accompanying subculture—however, there are many who use terms such
            as <em>turbo politics, turbo television, turbo architecture</em>{" "}
            and, what we’ll be examining here, <em>turbo-sculpture</em>.[50]
            [51] Key specimens of turbo-sculpture are the monuments I mentioned
            in my introduction: the Rocky Balboa in Žitište, the Bruce Lee in
            Mostar, and, of course, the Bob Marley in Banatski Sokolac.
          </>,
          <>
            It is pertinent to explain what makes <em>turbo</em>-sculpture,
            well, <em>turbo</em>, as the link between the works I mentioned
            previously and turbo-folk is not immediately clear. Over the years,
            the prefix turbo evolved to describe a broader aesthetic of kitsch,
            exaggeration and “random amalgamations of both local and global
            ornamentations”.[52] <em>Turbo</em>- here does not imply speed, but
            instead refers to an uncritical embrace of capitalist modernity. In
            addition, <em>turbo</em>- references a similar set of originating
            circumstances. Although the music genre appeared in the early-to-mid
            ‘90s and turbo-sculpture is a product of the early 2000s, they are
            both results of a prolonged crisis—the first coming to being at the
            start of the war that tore up Yugoslavia and the latter emerging in
            the war’s wake. The accompanying cultural vacuum and need for a new
            system of values is what led, partially, to this irreverent
            assemblage of Western and local culture. Lastly, I would point to
            the shared cultural status of all things <em>turbo</em>, that is to
            say the lack of any; they are all very obvious as products of
            working class culture, and as such are often the subject of ridicule
            from those supposedly sophisticated.
          </>,
        ],
      },
    },

    {
      header: { section: "endnotes", title: "Endnotes" },
      body: {
        variant: "endnotes",
        copy: [
          <>
            1. J. Bloom, ‘Bob Marley Statue for Serbia’,{" "}
            <i>The New York Times</i>, 25 August 2008,{" "}
            <a
              href="https://www.nytimes.com/2008/08/26/arts/26arts-BOBMARLEYSTA_BRF.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://www.nytimes.com/2008/08/26/arts/26arts-BOBMARLEYSTA_BRF.html
            </a>
            , (accessed 12 November 2019).
          </>,
          <>
            2. N. Cabric et al, ‘Balkan Monuments: The Weird and the Wonderful’,{" "}
            <i>Balkan Insight</i>, 25 June 2013,{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://balkaninsight.com/2013/06/25/balkan-monuments-the-weird-and-wonderful/"
            >
              https://balkaninsight.com/2013/06/25/balkan-monuments-the-weird-and-wonderful/
            </a>
            , (accessed 12 November 2019).
          </>,
          <>
            3. U. Čvoro,{" "}
            <i>
              Turbo-folk music and cultural representations of national identity
              in former Yugoslavia
            </i>
            , Routledge, 2014, p 132-133.
          </>,
          <>
            4. ‘Banatski Sokolac’, <i>Opština Plandište</i>, [website]{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="http://plandiste-opstina.rs/o-plandistu/naselja/banatski-sokolac/?script=lat"
            >
              http://plandiste-opstina.rs/o-plandistu/naselja/banatski-sokolac/?script=lat
            </a>
            , (accessed 18 December 2019).
          </>,
          <>
            5. ‘Rock Village 2008’, <i>last.fm</i> [website]
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.last.fm/festival/675960+Rock+Village+2008"
            >
              https://www.last.fm/festival/675960+Rock+Village+2008
            </a>{" "}
            (accessed 12 November 2019).
          </>,
          <>
            6. Cabric et al, “Balkan Monuments: The Weird and the Wonderful”.
          </>,
          <>
            7. ‘O Festivalu’, <i>Rock Village</i>, [website]{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="http://rockvillage.org/about/"
            >
              http://rockvillage.org/about/
            </a>{" "}
            (accessed 12 November 2019).
          </>,
          <>
            8. S. Michaels, ‘Bob Marley statue unites Serbs and Croats’,{" "}
            <i>The Guardian</i>, 25 August 2008,{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.theguardian.com/music/2008/aug/25/bob.marley.statue.serbia"
            >
              https://www.theguardian.com/music/2008/aug/25/bob.marley.statue.serbia
            </a>
            (accessed 12 November 2019).
          </>,
          <>
            9. ‘Bosnia unveils Bruce Lee bronze’, <i>BBC News</i>, 26 November
            2005,{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="http://news.bbc.co.uk/2/hi/entertainment/4474316.stm"
            >
              http://news.bbc.co.uk/2/hi/entertainment/4474316.stm
            </a>{" "}
            (accessed 12 November 2019).
          </>,
          <>
            10. It is perhaps interesting that this act of vandalism echoes a
            previous act of destruction; in 1993, Croat military forces blew up{" "}
            <i>Stari Most</i>, an Ottoman bridge of great symbolic and
            architectural significance. Similar to the Bruce Lee, the bridge was
            a symbol of unity and reconciliation. It was rebuilt after the war
            and was re-opened for the public in 2004.
          </>,
          <>
            11. Z. Volcic, ‘The Struggle to Express, Create and Represent in the
            Balkans’, in H. K. Anheier, Y. R. Isar (eds.),{" "}
            <i>Cultural Expression, Creativity and Innovation</i>, 2010, SAGE
            Publications, vol. 3, p 163.
          </>,
          <>
            12. D. Bilefsky, ‘Balkans’ Idolatry Delights Movie Fans and
            Pigeons’, <i>The New York Times</i>, 11 November 2007,{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.nytimes.com/2007/11/11/world/europe/11balkans.html"
            >
              https://www.nytimes.com/2007/11/11/world/europe/11balkans.html
            </a>{" "}
            (accessed 12 November 2019).
          </>,
          <>
            13. Čvoro, <i>Turbo-folk music</i>, p 130.
          </>,
          <>
            14. Volcic, ‘The Struggle to Express, Create and Represent in the
            Balkans’, p 163-164.
          </>,
          <>
            15. A more detailed analysis of this pattern can be found in M.
            Pourgouris, ‘<i>Ulysses’ Gaze</i> and the myth of Balkan history’ in
            T. Aleksić (ed.),{" "}
            <i>Mythistory and Narratives of the Nation in the Balkans</i>,
            Cambridge Scholars Press, 2007, p 170-194.
          </>,
          <>
            16. W. Hoepken, ‘War, Memory, and Education in a Fragmented Society:
            The Case of Yugoslavia’, in{" "}
            <i>East European Politics and Societies</i>, vol. 13, no. 1, 1998, p
            190-227.
          </>,
          <>
            17. The word <i>veneration</i> is truly appropriate, as the homage
            paid to certain figures seems more fitting to a saint or a god than
            a military commander. For example, in the house where Pavlos Melas,
            a Greek hero in the Macedonian Struggle, died, a framed inscription
            written by a kindergarten teacher reads:
            <blockquote>
              <i>“Bow</i>. For the stranger traveller who treads on this{" "}
              <i>sacred</i> place, and [lifts] his <i>soul</i> closer to the big
              hero… […] He was a fighter. And when he was in battle, he threw
              away the mask of the peaceful private man, like Achilles.”
              [italics mine]
            </blockquote>
            Not only does this inscription evoke religious pilgrimages, but it
            also includes a direct comparison between Melas and a mythic warrior
            demigod. From: A. Karakasidou, ‘Affections of a Greek Hero: Pavlos
            Melas and heroic representations in Greece’, in M. Todorova (ed),{" "}
            <i>Balkan Identities: Nation and Memory</i>, Hurst and Company,
            2004, p 208.
          </>,
          <>
            18. I. Čolović, ‘A Criminal-National hero? But who else?’, in M.
            Todorova (ed), <i>Balkan Identities: Nation and Memory</i>, Hurst
            and Company, 2004, p 253-268.
          </>,
          <>
            19. Čvoro, <i>Turbo-folk music</i>, p 131.
          </>,
          <>
            20. Čvoro, <i>Turbo-folk music</i>, p 140.
          </>,
          <>
            21. D. Niebyl, <i>Spomenik Monument Database</i>, FUEL Publishing,
            2018, p 6.
          </>,
          <>
            22. M. Jauković, ‘To Share or to Keep: The Afterlife of Yugoslavia’s
            Heritage and the Contemporary Heritage Management Practices’,{" "}
            <i>Croatian Political Science Review</i>, vol. 51, no. 4, 2014, p
            85.
          </>,
          <>
            23. D. Niebyl, ‘What are their present-day conditions?’,{" "}
            <i>Spomenik Database</i>, [website]{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.spomenikdatabase.org/present-day-conditions"
            >
              https://www.spomenikdatabase.org/present-day-conditions
            </a>
            , 2016, (accessed 1 December 2019).
          </>,
          <>
            24. Niebyl, <i>Spomenik Monument Database</i>, p 7.
          </>,
          <>
            25. J. E. Young, ‘The Monument in Ruins’, in Independent Curators
            Incorporated (ed.) <i>Monumental Propaganda</i>, Independent
            Curators Incorporated, 1994, p 67.
          </>,
          <>
            26. “Banatski Sokolac”, <i>Opština Plandište</i>.
          </>,
          <>
            27. Komar & Melamid, ‘Letter to Boris Yeltsin’, in Independent
            Curators Incorporated (ed.), <i>Monumental Propaganda</i>,
            Independent Curators Incorporated, 1994, p 11.
          </>,
          <>28. J. E. Young, ‘The Monument in Ruins’, p 70.</>,
          <>
            29. Q. Stevens, K. A. Franck, R. Fazakerley, ‘Counter-Monuments: The
            Anti-Monumental and the Dialogic’,{" "}
            <i>The Journal of Architecture</i>, vol. 17, no. 6, 2012, p 951,
            972.
          </>,
          <>
            30. Stevens, Franck, Fazakerley, ‘Counter-Monuments: The
            Anti-Monumental and the Dialogic’, p 958.
          </>,
          <>
            31. Čvoro, <i>Turbo-folk music</i>, p 2.
          </>,
          <>
            32. Čvoro, <i>Turbo-folk music</i>, p 6.
          </>,
          <>
            33. L. V. Rasmussen, ‘From Source to Commodity: Newly-Composed Folk
            Music of Yugoslavia’, <i>Popular Music</i>, vol. 14, no.2, 1995, p
            245.
          </>,
          <>
            34. R. Archer, ‘Assessing Turbofolk Controversies: Popular Music
            between the Nation and the Balkans’, <i>Southeastern Europe</i>,
            vol. 36, no. 2, 2012, p 181.
          </>,
          <>
            35. Čvoro, <i>Turbo-folk music</i>, p 9.
          </>,
          <>
            36. Čvoro, <i>Turbo-folk music</i>, p 11.
          </>,
          <>
            37. I. Kronja, ‘Turbo Folk and Dance Music in 1990s Serbia: Media,
            Ideology and the Production of Spectacle’,{" "}
            <i>The Anthropology of East Europe Review</i>, vol. 22, no. 1, 2004,
            p 104.
          </>,
          <>
            38. Z. Volčič, K. Erjavec, ‘The Paradox of Ceca and the Turbo-Folk
            Audience’, <i>Popular Communication</i>, vol. 8, no.2, 2010, p 107.
          </>,
          <>
            39. B. Dimitrijević, ‘"Turbo-Folk Made Me Do It!":
            Schizo-anthropology of the Genre’, lecture at Moderna Gallerija,
            Ljubljana, 26 August 2019,{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="http://mrezni-muzej.mg-lj.si/en/networkmuseum/3/23/?artworkid=1431"
            >
              http://mrezni-muzej.mg-lj.si/en/networkmuseum/3/23/?artworkid=1431
            </a>
            , (accessed 23 November 2019).
          </>,
          <>
            40. Čvoro, <i>Turbo-folk music</i>, p 61-70.
          </>,
          <>
            41. I. Kronja, ‘
            <i>Smrtonosni sjaj: masovna psihologija i estetika turbo-folka</i>’
            (‘The Fatal Glow: Mass Psychology and Aesthetics of Turbo-folk
            Subculture’), Belgrade, Tehnocratia, 2001, cited in U. Čvoro,{" "}
            <i>
              Turbo-folk music and cultural representations of national identity
              in former Yugoslavia
            </i>
            , Routledge, 2014, p 17.
          </>,
          <>
            42. Archer, ‘Assessing Turbofolk Controversies: Popular Music
            between the Nation and the Balkans’.
          </>,
          <>
            43. Čvoro, <i>Turbo-folk music</i>, p 74.
          </>,
          <>
            44. Čvoro, <i>Turbo-folk music</i>, p 89.
          </>,
          <>
            45. The <i>Balkanist</i>, an online cultural platform, features an
            eight-part series entitled “Queer as Turbo-folk” exploring various
            aspects of this particular topic—which is beyond the scope of this
            essay. The first instalment can be found here:{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://balkanist.net/queer-as-turbofolk-part-i-eastern-europe-is-homophobic/"
            >
              https://balkanist.net/queer-as-turbofolk-part-i-eastern-europe-is-homophobic/
            </a>{" "}
            (Accessed 21 November 2019).
          </>,
          <>
            46. The relationship between popular music such as turbo-folk and
            Yugoslav nationalism is something often explored in Dragojević’s
            films; additional examples can be found in his <i>Rane</i> [Wounds,
            1998] and the classic <i>Lepa Sela Lepo Gore</i> [Pretty Village,
            Pretty Flame, 1996].
          </>,
          <>
            47. B. Dimitrijević, ‘"Turbo-Folk Made Me Do It!":
            Schizo-anthropology of the Genre’.
          </>,
          <>
            48. C. Baker, ‘The concept of turbofolk in Croatia:
            inclusion/exclusion of national musical identity’, in{" "}
            <i>
              Nation in formation: inclusion and exclusion in central and
              eastern Europe
            </i>
            , ed. by C. Baker et al., London: SSEES Occasional Papers , 2007, p.
            139.
          </>,
          <>
            49. T. Longinović, ‘Post-Oriental Avliya: Translating the Balkans
            into <i>Globalese</i>.’, in{" "}
            <i>Mythistory and Narratives of the Nation in the Balkans</i>, ed.
            by T. Aleksić, Cambridge Scholars Press, 2007, p. 155-169.
          </>,
          <>
            50. Čvoro, <i>Turbo-folk music</i>.
          </>,
          <>
            51. A. Domanović, <i>Turbo Sculpture, 2010 - 2013</i>, [online
            video], 2014,{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://vimeo.com/95907707"
            >
              https://vimeo.com/95907707
            </a>
            , (accessed 28 November 2019).
          </>,
          <>
            52. VVORK, ‘IMG MGMT: Turbo sculpture’, <i>Art F City</i>, 24 August
            2009,{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="http://artfcity.com/2009/08/24/img-mgmt-turbo-sculpture/"
            >
              http://artfcity.com/2009/08/24/img-mgmt-turbo-sculpture/
            </a>{" "}
            (accessed 28 November 2019).
          </>,
        ],
      },
    },
    {
      header: { section: "bibliography", title: "Bibliography" },
      body: {
        variant: "endnotes",
        copy: [
          <>
            ‘Banatski Sokolac’, <i>Opština Plandište</i>, [website]{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="http://plandiste-opstina.rs/o-plandistu/naselja/banatski-sokolac/?script=lat"
            >
              http://plandiste-opstina.rs/o-plandistu/naselja/banatski-sokolac/?script=lat
            </a>
            , (accessed 18 December 2019).
          </>,
          <>
            ‘Bosnia unveils Bruce Lee bronze’, <i>BBC News</i>, 26 November
            2005,{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="http://news.bbc.co.uk/2/hi/entertainment/4474316.stm"
            >
              http://news.bbc.co.uk/2/hi/entertainment/4474316.stm
            </a>{" "}
            (accessed 12 November 2019).
          </>,
          <>
            ‘O Festivalu’, <i>Rock Village</i>, [website]{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="http://rockvillage.org/about/"
            >
              http://rockvillage.org/about/
            </a>{" "}
            (accessed 12 November 2019).
          </>,
          <>
            ‘Rock Village 2008’, <i>last.fm</i> [website]{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.last.fm/festival/675960+Rock+Village+2008"
            >
              https://www.last.fm/festival/675960+Rock+Village+2008
            </a>{" "}
            (accessed 12 November 2019).
          </>,
          <>
            Anderson, B., <i>Imagined Communities</i>, Verso, 1983.
          </>,
          <>
            Archer, R., ‘Assessing Turbofolk Controversies: Popular Music
            between the Nation and the Balkans’, <i>Southeastern Europe</i>,
            vol. 36, no. 2, 2012.
          </>,
          <>
            Argyropoulou, N. and Tzirtzilakis, Y. (eds.),{" "}
            <i>Hell As Pavillion</i>, Kastaniotis, 2013.
          </>,
          <>
            Badovinac, Z. (ed.),{" "}
            <i>Body and the East: From the 1960s to the Present</i>, MIT Press,
            1999.
          </>,
          <>
            Baker, C., ‘Music as a Weapon of Ethnopolitical Violence and
            Conflict: Processes of Ethnic Separation during and after the
            Break-up of Yugoslavia’, <i>Patterns of Prejudice</i>, vol. 47, no
            4-5, 2013, p 409-429.
          </>,
          <>
            Baker, C., ‘The concept of turbofolk in Croatia: inclusion/exclusion
            of national musical identity’, in C. Baker et al (eds.),{" "}
            <i>
              Nation in formation: inclusion and exclusion in central and
              eastern Europe
            </i>
            , SSEES Occasional Papers , 2007, p. 139.
          </>,
          <>
            Begić, S. and B. Mraović, ‘Forsaken Monuments and Social Change: The
            Function of Socialist Monuments in the Post-Yugoslav Space’, in S.
            L. Moeschberger, and R. A. L. DeZalia (eds.),{" "}
            <i>Symbols That Bind, Symbols That Divide</i>, Springer
            International Publishing, 2014, p. 13-37.
          </>,
          <>
            Bilefsky, D., ‘Balkans’ Idolatry Delights Movie Fans and Pigeons’,{" "}
            <i>The New York Times</i>, 11 November 2007,{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.nytimes.com/2007/11/11/world/europe/11balkans.html"
            >
              https://www.nytimes.com/2007/11/11/world/europe/11balkans.html
            </a>{" "}
            (accessed 12 November 2019).
          </>,
          <>
            Bjelić, D. I. and O. Savić (eds.),{" "}
            <i>Balkan as Metaphor: Between Globalization and Fragmentation</i>,
            MIT Press, 2002.
          </>,
          <>
            Bloom, J., ‘Bob Marley Statue for Serbia’, <i>The New York Times</i>
            , 25 August 2008,{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.nytimes.com/2008/08/26/arts/26arts-BOBMARLEYSTA_BRF.html"
            >
              https://www.nytimes.com/2008/08/26/arts/26arts-BOBMARLEYSTA_BRF.html
            </a>
            , (accessed 12 November 2019).
          </>,
          <>
            Brown, M. E. (ed.),{" "}
            <i>Ethnic Conflict and International Security</i>, Princeton
            University Press, 1993.
          </>,
          <>
            Cabric, N. et al, ‘Balkan Monuments: The Weird and the Wonderful’,{" "}
            <i>Balkan Insight</i>, 25 June 2013,{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://balkaninsight.com/2013/06/25/balkan-monuments-the-weird-and-wonderful/"
            >
              https://balkaninsight.com/2013/06/25/balkan-monuments-the-weird-and-wonderful/
            </a>
            , (accessed 12 November 2019)
          </>,
          <>
            Calotychos, V.,{" "}
            <i>
              The Balkan Prospect: Identity, Culture, and Politics in Greece
              after 1989
            </i>
            , Palgrave Macmillan, 2013.
          </>,
          <>
            Čolović, I., ‘A Criminal-National hero? But who else?’, in M.
            Todorova (ed), <i>Balkan Identities: Nation and Memory</i>, Hurst
            and Company, 2004.
          </>,
          <>
            Cowan, J. K. (ed.),{" "}
            <i>Macedonia: The Politics of Identity and Difference</i>, Pluto
            Press, 2000.
          </>,
          <>
            Critchley, S., <i>On Humour</i>, Routledge, 2002.
          </>,
          <>
            Čvoro, U.,{" "}
            <i>
              Turbo-folk music and cultural representations of national identity
              in former Yugoslavia
            </i>
            , Routledge, 2014.
          </>,
          <>
            Danopoulos, C. P. and K. G. Messas (eds.),{" "}
            <i>Crises in the Balkans</i>, Westview Press, 1997.
          </>,
          <>
            Dimitrijević, B., ‘"Turbo-Folk Made Me Do It!": Schizo-anthropology
            of the Genre’, lecture at Moderna Gallerija, Ljubljana, 26 August
            2019,{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="http://mrezni-muzej.mg-lj.si/en/networkmuseum/3/23/?artworkid=1431"
            >
              http://mrezni-muzej.mg-lj.si/en/networkmuseum/3/23/?artworkid=1431
            </a>
            , (accessed 23 November 2019).
          </>,
          <>
            Djokić, D. (ed.),{" "}
            <i>Yugoslavism: Histories of a Failed Idea 1918-1992</i>, C. Hurst &
            Co., 2003.
          </>,
          <>
            Dogo, M. and G. Franzinetti (eds.),{" "}
            <i>
              Disrupting and Reshaping: Early Stages of Nation-Building in the
              Balkans
            </i>
            , Editore Ravenna, 2002.
          </>,
          <>
            Domanović, A., <i>Turbo Sculpture, 2010 - 2013</i>, [online video],
            2014,{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://vimeo.com/95907707"
            >
              https://vimeo.com/95907707
            </a>
            , (Accessed 28 November 2019).
          </>,
          <>
            Graubard, S. R. (ed.), <i>A New Europe for the Old?</i>, Transaction
            Publishers, 1999.
          </>,
          <>
            Hatzopoulos, P.,{" "}
            <i>
              The Balkans Beyond Nationalism and Identity: International
              Relations and Ideology
            </i>
            , I.B.Tauris, 2008.
          </>,
          <>
            Hlavajova, M. and S. Sheikh (eds.),{" "}
            <i>Former West: Art and the Contemporary After 1989</i>, MIT Press,
            2016.
          </>,
          <>
            Hoepken, W., ‘War, Memory, and Education in a Fragmented Society:
            The Case of Yugoslavia’, in{" "}
            <i>East European Politics and Societies</i>, vol. 13, no. 1, 1998.
          </>,
          <>
            Jauković, M., ‘To Share or to Keep: The Afterlife of Yugoslavia’s
            Heritage and the Contemporary Heritage Management Practices’,{" "}
            <i>Croatian Political Science Review</i>, vol. 51, no. 4, 2014.
          </>,
          <>
            Karakasidou, A., ‘Affections of a Greek Hero: Pavlos Melas and
            heroic representations in Greece’, in M. Todorova (ed),{" "}
            <i>Balkan Identities: Nation and Memory</i>, Hurst and Company,
            2004.
          </>,
          <>
            Kisić, V., <i>Governing Heritage Dissonance</i>, European Cultural
            Foundation, 2013.
          </>,
          <>
            Komar & Melamid, ‘Letter to Boris Yeltsin’, in Independent Curators
            Incorporated (ed.), <i>Monumental Propaganda</i>, Independent
            Curators Incorporated, 1994.
          </>,
          <>
            Kronja, I., ‘Smrtonosni sjaj: masovna psihologija i estetika
            turbo-folka’ (‘The Fatal Glow: Mass Psychology and Aesthetics of
            Turbo-folk Subculture’), Belgrade, Tehnocratia, 2001, cited in U.
            Čvoro,{" "}
            <i>
              Turbo-folk music and cultural representations of national identity
              in former Yugoslavia
            </i>
            , Routledge, 2014.
          </>,
          <>
            Kronja, I., ‘Turbo Folk and Dance Music in 1990s Serbia: Media,
            Ideology and the Production of Spectacle’,{" "}
            <i>The Anthropology of East Europe Review</i>, vol. 22, no. 1, 2004.
          </>,
          <>
            Lampe, J. and M. Mazower (eds.),{" "}
            <i>
              Ideologies and National Identities: The Case of Twentieth-Century
              Southeastern Europe
            </i>
            , Central European University Press, 2006.
          </>,
          <>
            Longinović, T., ‘Post-Oriental Avliya: Translating the Balkans into
            Globalese.’, in T. Aleksić (ed.),{" "}
            <i>Mythistory and Narratives of the Nation in the Balkans</i>,
            Cambridge Scholars Press, 2007, p. 155-169.
          </>,
          <>
            Michaels, S., ‘Bob Marley statue unites Serbs and Croats’,{" "}
            <i>The Guardian</i>, 25 August 2008,{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.theguardian.com/music/2008/aug/25/bob.marley.statue.serbia"
            >
              https://www.theguardian.com/music/2008/aug/25/bob.marley.statue.serbia
            </a>{" "}
            (accessed 12 November 2019).
          </>,
          <>
            Mlekuž, J., <i>Burek: A Culinary Metaphor</i>, Budapest: Central
            European University Press, 2015.
          </>,
          <>
            Niebyl, D., ‘What are their present-day conditions?’,{" "}
            <i>Spomenik Database</i>, [website]{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.spomenikdatabase.org/present-day-conditions"
            >
              https://www.spomenikdatabase.org/present-day-conditions
            </a>
            , 2016, (accessed 1 December 2019).
          </>,
          <>
            Niebyl, D., <i>Spomenik Monument Database</i>, FUEL Publishing,
            2018.
          </>,
          <>
            Pavlowitch, S. K., <i>A History of the Balkans: 1804-1945</i>,
            Addison Wesley Longman, 1999.
          </>,
          <>
            Poulton, H., <i>The Balkans: Minorities and States in Conflict</i>,
            Minority Rights Publications, 1993.
          </>,
          <>
            Pourgouris, M., ‘Ulysses’ Gaze and the myth of Balkan history’ in T.
            Aleksić (ed.),{" "}
            <i>Mythistory and Narratives of the Nation in the Balkans</i>,
            Cambridge Scholars Press, 2007.
          </>,
          <>
            Ramet, S. P., <i>Balkan Babel</i>, Westview Press, 1999.
          </>,
          <>
            Rasmussen, L. V., ‘From Source to Commodity: Newly-Composed Folk
            Music of Yugoslavia’, <i>Popular Music</i>, vol. 14, no.2, 1995.
          </>,
          <>
            Schierup, C.-U. (ed.),{" "}
            <i>
              Scramble for the Balkans: nationalism, globalism and the political
              economy of reconstruction
            </i>
            , Macmillan Press Ltd, 1999.
          </>,
          <>
            Stevens, Q., K. A. Franck, R. Fazakerley, ‘Counter-Monuments: The
            Anti-Monumental and the Dialogic’,{" "}
            <i>The Journal of Architecture</i>, vol. 17, no. 6, 2012, p 951.
          </>,
          <>
            Šuvaković, M., ‘Impossible Histories’, in D. Djurić and M. Šuvaković
            (eds.),{" "}
            <i>
              Impossible Histories: Historical Avant-Gardes, Neo-Avant-Gardes,
              and Post-Avant-Gardes in Yugoslavia
            </i>
            , 1918-1991, MIT Press, 2003.
          </>,
          <>
            Todorova, M., <i>Imagining the Balkans</i>, updated edn, Oxford
            University Press, 2009.
          </>,
          <>
            Ugrešić, D., <i>The Culture of Lies</i>, Pennsylvania State
            University Press, 1998.
          </>,
          <>
            Volčič, Z. and K. Erjavec, ‘The Paradox of Ceca and the Turbo-Folk
            Audience’, <i>Popular Communication</i>, vol. 8, no.2, 2010.
          </>,
          <>
            Volcic, Z., ‘The Struggle to Express, Create and Represent in the
            Balkans’, in H. K. Anheier, Y. R. Isar (eds.),{" "}
            <i>Cultural Expression, Creativity and Innovation</i>, 2010, SAGE
            Publications, vol. 3.
          </>,
          <>
            VVORK, ‘IMG MGMT: Turbo sculpture’, <i>Art F City</i>, 24 August
            2009,{" "}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="http://artfcity.com/2009/08/24/img-mgmt-turbo-sculpture/"
            >
              http://artfcity.com/2009/08/24/img-mgmt-turbo-sculpture/
            </a>{" "}
            (accessed 28 November 2019).
          </>,
          <>
            Young, J. E., ‘The Monument in Ruins’, in Independent Curators
            Incorporated (ed.) <i>Monumental Propaganda</i>, Independent
            Curators Incorporated, 1994.
          </>,
          <>
            Αντωνιάδης, Χ. (ed.), <i>Βαλκάνια, η Μεγάλη Τρικυμία</i>, Εκδόσεις
            Κασταλία, 2018.
          </>,
        ],
      },
    },

    {
      header: { section: "colophon", title: "Colophon" },
      body: {
        variant: "paragraph",
        copy: [
          <>
            Essay by Alexandra Papademetriou, May 2021, on the occasion of the
            exhibition <i>Monument to Balkan Brotherhood</i>, DC Krov, Belgrade.
          </>,
          "Originally written in 2019 within the context of the Fine Art MFA of HDK-Valand, University of Gothenburg.",
          "Page built by Charlotte Curran.",

          <>
            <a
              rel="license noopener noreferrer"
              href="http://creativecommons.org/licenses/by/4.0/"
              target="_blank"
            >
              <img
                alt="Creative Commons License"
                style={{ borderWidth: "0" }}
                src="https://i.creativecommons.org/l/by/4.0/80x15.png"
              />
            </a>
            <br />
            This work is licensed under a{" "}
            <a
              rel="license noopener noreferrer"
              href="http://creativecommons.org/licenses/by/4.0/"
              target="_blank"
            >
              Creative Commons Attribution 4.0 International License.
            </a>
          </>,
        ],
      },
    },
  ],
};

export const turboSculptureEndnotes = {
  1: "1.  J. Bloom, ‘Bob Marley Statue for Serbia’, The New York Times, 25 August 2008, https://www.nytimes.com/2008/08/26/arts/26arts-BOBMARLEYSTA_BRF.html, (accessed 12 November 2019).",
  2: "2.  N. Cabric et al, ‘Balkan Monuments: The Weird and the Wonderful’, Balkan Insight, 25 June 2013, https://balkaninsight.com/2013/06/25/balkan-monuments-the-weird-and-wonderful/, (accessed 12 November 2019).",
  3: "3.  U. Čvoro, Turbo-folk music and cultural representations of national identity in former Yugoslavia, Routledge, 2014, p 132-133.",
  4: "4.  ‘Banatski Sokolac’, Opština Plandište, [website] http://plandiste-opstina.rs/o-plandistu/naselja/banatski-sokolac/?script=lat, (accessed 18 December 2019).",
  5: "5.  ‘Rock Village 2008’, last.fm [website] https://www.last.fm/festival/675960+Rock+Village+2008 (accessed 12 November 2019).",
  6: "6.  Cabric et al, “Balkan Monuments: The Weird and the Wonderful”.",
  7: "7.  ‘O Festivalu’, Rock Village, [website] http://rockvillage.org/about/ (accessed 12 November 2019).",
  8: "8.  S. Michaels, ‘Bob Marley statue unites Serbs and Croats’, The Guardian, 25 August 2008,  https://www.theguardian.com/music/2008/aug/25/bob.marley.statue.serbia (accessed 12 November 2019).",
  9: "9.  ‘Bosnia unveils Bruce Lee bronze’, BBC News, 26 November 2005, http://news.bbc.co.uk/2/hi/entertainment/4474316.stm (accessed 12 November 2019).",
  10: "10.  It is perhaps interesting that this act of vandalism echoes a previous act of destruction; in 1993, Croat military forces blew up Stari Most, an Ottoman bridge of great symbolic and architectural significance. Similar to the Bruce Lee, the bridge was a symbol of unity and reconciliation. It was rebuilt after the war and was re-opened for the public in 2004.",
  11: "11.  Z. Volcic, ‘The Struggle to Express, Create and Represent in the Balkans’, in H. K. Anheier, Y. R. Isar (eds.), Cultural Expression, Creativity and Innovation, 2010, SAGE Publications, vol. 3, p 163.",
  12: "12.  D. Bilefsky, ‘Balkans’ Idolatry Delights Movie Fans and Pigeons’, The New York Times, 11 November 2007, https://www.nytimes.com/2007/11/11/world/europe/11balkans.html (accessed 12 November 2019).",
  13: "13.  Čvoro, Turbo-folk music, p 130.",
  14: "14.  Volcic, ‘The Struggle to Express, Create and Represent in the Balkans’, p 163-164.",
  15: "15.  A more detailed analysis of this pattern can be found in M. Pourgouris, ‘Ulysses’ Gaze and the myth of Balkan history’ in T. Aleksić (ed.), Mythistory and Narratives of the Nation in the Balkans, Cambridge Scholars Press, 2007, p 170-194.",
  16: "16.  W. Hoepken, ‘War, Memory, and Education in a Fragmented Society: The Case of Yugoslavia’, in East European Politics and Societies, vol. 13, no. 1, 1998, p 190-227.",
  17: "17.  The word veneration is truly appropriate, as the homage paid to certain figures seems more fitting to a saint or a god than a military commander. For example, in the house where Pavlos Melas, a Greek hero in the Macedonian Struggle, died, a framed inscription written by a kindergarten teacher reads: “Bow. For the stranger traveller who treads on this sacred place, and [lifts] his soul closer to the big hero… […] He was a fighter. And when he was in battle, he threw away the mask of the peaceful private man, like Achilles.” [italics mine] Not only does this inscription evoke religious pilgrimages, but it also includes a direct comparison between Melas and a mythic warrior demigod. From: A. Karakasidou, ‘Affections of a Greek Hero: Pavlos Melas and heroic representations in Greece’, in M. Todorova (ed), Balkan Identities: Nation and Memory, Hurst and Company, 2004, p 208.",
  18: "18.  I. Čolović, ‘A Criminal-National hero? But who else?’, in M. Todorova (ed), Balkan Identities: Nation and Memory,  Hurst and Company, 2004, p 253-268.",
  19: "19.  Čvoro, Turbo-folk music, p 131.",
  20: "20.  Čvoro, Turbo-folk music, p 140.",
  21: "21.  D. Niebyl, Spomenik Monument Database, FUEL Publishing, 2018, p 6.",
  22: "22.  M. Jauković, ‘To Share or to Keep: The Afterlife of Yugoslavia’s Heritage and the Contemporary Heritage Management Practices’, Croatian Political Science Review, vol. 51, no. 4, 2014, p 85.",
  23: "23.  D. Niebyl, ‘What are their present-day conditions?’, Spomenik Database, [website] https://www.spomenikdatabase.org/present-day-conditions, 2016, (accessed 1 December 2019).",
  24: "24.  Niebyl, Spomenik Monument Database, p 7.",
  25: "25.  J. E. Young, ‘The Monument in Ruins’, in Independent Curators Incorporated (ed.) Monumental Propaganda, Independent Curators Incorporated, 1994, p 67.",
  26: "26.  “Banatski Sokolac”, Opština Plandište.",
  27: "27.  Komar & Melamid, ‘Letter to Boris Yeltsin’, in Independent Curators Incorporated (ed.), Monumental Propaganda, Independent Curators Incorporated, 1994, p 11.",
  28: "28.  J. E. Young, ‘The Monument in Ruins’, p 70.",
  29: "29.  Q. Stevens, K. A. Franck, R. Fazakerley, ‘Counter-Monuments: The Anti-Monumental and the Dialogic’, The Journal of Architecture, vol. 17, no. 6, 2012, p 951, 972.",
  30: "30.  Stevens, Franck, Fazakerley, ‘Counter-Monuments: The Anti-Monumental and the Dialogic’, p 958.",
  31: "31.  Čvoro, Turbo-folk music, p 2.",
  32: "32.  Čvoro, Turbo-folk music, p 6.",
  33: "33.  L. V. Rasmussen, ‘From Source to Commodity: Newly-Composed Folk Music of Yugoslavia’, Popular Music, vol. 14, no.2, 1995, p 245.",
  34: "34.  R. Archer, ‘Assessing Turbofolk Controversies: Popular Music between the Nation and the Balkans’, Southeastern Europe, vol. 36, no. 2, 2012, p 181.",
  35: "35.  Čvoro, Turbo-folk music, p 9.",
  36: "36.  Čvoro, Turbo-folk music, p 11.",
  37: "37.  I. Kronja, ‘Turbo Folk and Dance Music in 1990s Serbia: Media, Ideology and the Production of Spectacle’, The Anthropology of East Europe Review, vol. 22, no. 1, 2004, p 104.",
  38: "38.  Z. Volčič, K. Erjavec, ‘The Paradox of Ceca and the Turbo-Folk Audience’, Popular Communication, vol. 8, no.2, 2010, p 107.",
  39: `39.  B. Dimitrijević, ‘"Turbo-Folk Made Me Do It!": Schizo-anthropology of the Genre’, lecture at Moderna Gallerija, Ljubljana, 26 August 2019, http://mrezni-muzej.mg-lj.si/en/networkmuseum/3/23/?artworkid=1431, (accessed 23 November 2019).`,
  40: "40.  Čvoro, Turbo-folk music, p 61-70.",
  41: "41.  I. Kronja, ‘Smrtonosni sjaj: masovna psihologija i estetika turbo-folka’ (‘The Fatal Glow: Mass Psychology and Aesthetics of Turbo-folk Subculture’), Belgrade, Tehnocratia, 2001, cited in U. Čvoro, Turbo-folk music and cultural representations of national identity in former Yugoslavia, Routledge, 2014, p 17.",
  42: "42.  Archer, ‘Assessing Turbofolk Controversies: Popular Music between the Nation and the Balkans’.",
  43: "43.  Čvoro, Turbo-folk music, p 74.",
  44: "44.  Čvoro, Turbo-folk music, p 89.",
  45: "45.  The Balkanist, an online cultural platform, features an eight-part series entitled “Queer as Turbo-folk” exploring various aspects of this particular topic—which is beyond the scope of this essay. The first instalment can be found here: https://balkanist.net/queer-as-turbofolk-part-i-eastern-europe-is-homophobic/ (Accessed 21 November 2019).",
  46: "46.  The relationship between popular music such as turbo-folk and Yugoslav nationalism is something often explored in Dragojević’s films; additional examples can be found in his Rane [Wounds, 1998] and the classic Lepa Sela Lepo Gore [Pretty Village, Pretty Flame, 1996].",
  47: `47.  B. Dimitrijević, ‘"Turbo-Folk Made Me Do It!": Schizo-anthropology of the Genre’."`,
  48: "48.  C. Baker, ‘The concept of turbofolk in Croatia: inclusion/exclusion of national musical identity’, in Nation in formation: inclusion and exclusion in central and eastern Europe, ed. by C. Baker et al., London: SSEES Occasional Papers , 2007, p. 139.",
  49: "49.  T. Longinović, ‘Post-Oriental Avliya: Translating the Balkans into Globalese.’, in Mythistory and Narratives of the Nation in the Balkans, ed. by T. Aleksić, Cambridge Scholars Press, 2007, p. 155-169.",
  50: "50.  Čvoro, Turbo-folk music.",
  51: "51.  A. Domanović, Turbo Sculpture, 2010 - 2013, [online video], 2014, https://vimeo.com/95907707, (accessed 28 November 2019).",
  52: "52.  VVORK, ‘IMG MGMT: Turbo sculpture’, Art F City, 24 August 2009, http://artfcity.com/2009/08/24/img-mgmt-turbo-sculpture/ (accessed 28 November 2019).",
};
